import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Pagination, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, TextField, Button, Grid,
} from '@mui/material';
import {
  fetchAllFeedback,
  solveTicket,
  ProductFeedback,
  AllFeedbackFilters,
  deleteTicket
} from '../../functions/productFeedbackApi';
import TicketDetailModal from '../HelpDesk/TicketDetailModal';
import NoDataFound from '../common/NoDataFound';
import { fetchOrganisations } from '../../services/organisations.service';

const TYPE_COLORS: Record<string, { main: string; bg: string }> = {
  bug:         { main: '#B91C1C', bg: '#FEE2E2' },
  enhancement: { main: '#6D28D9', bg: '#EDE9FE' },
  feature:     { main: '#1D4ED8', bg: '#DBEAFE' },
};

const PRIORITY_COLORS: Record<string, { main: string; bg: string }> = {
  high:   { main: '#BE123C', bg: '#FFE4E6' },
  medium: { main: '#475569', bg: '#E2E8F0' },
  low:    { main: '#047857', bg: '#D1FAE5' },
};

const STATUS_COLORS: Record<string, { main: string; bg: string }> = {
  open:   { main: '#4338CA', bg: '#E0E7FF' },
  solved: { main: '#065F46', bg: '#D1FAE5' },
};

const TYPE_LABELS: Record<string, string> = {
  bug: 'Bug / Issue',
  enhancement: 'Enhancement',
  feature: 'New Feature',
};

const ITEMS_PER_PAGE = 20;

const ProductFeedbackAdminView: React.FC = () => {
  const [tickets, setTickets]           = useState<ProductFeedback[]>([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [loading, setLoading]           = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<ProductFeedback | null>(null);

  // Filter state
  const [orgList, setOrgList]       = useState<{ _id: string; name: string }[]>([]);
  const [filterOrgId, setFilterOrgId] = useState('');
  const [filterType, setFilterType]   = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus]     = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]     = useState('');

  // Load org list once for the org dropdown
  useEffect(() => {
    (async () => {
      try {
        const resp: any = await fetchOrganisations();
        setOrgList(resp?.organisations || []);
      } catch {
        // silently fail — dropdown just stays empty
      }
    })();
  }, []);

  const buildFilters = useCallback((): AllFeedbackFilters => ({
    orgId:    filterOrgId    || undefined,
    type:     filterType     || undefined,
    priority: filterPriority || undefined,
    status:   filterStatus   || undefined,
    dateFrom: filterDateFrom || undefined,
    dateTo:   filterDateTo   || undefined,
  }), [filterOrgId, filterType, filterPriority, filterStatus, filterDateFrom, filterDateTo]);

  const loadTickets = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const resp = await fetchAllFeedback(buildFilters(), p, ITEMS_PER_PAGE);
      setTickets(resp.data);
      setTotal(resp.total);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  useEffect(() => {
    setPage(1);
    loadTickets(1);
  }, [filterOrgId, filterType, filterPriority, filterStatus, filterDateFrom, filterDateTo]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    loadTickets(value);
  };

  const handleClearFilters = () => {
    setFilterOrgId('');
    setFilterType('');
    setFilterPriority('');
    setFilterStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const handleSolve = async (id: string) => {
    const resp: any = await solveTicket(id);
    if (resp?.success) {
      // Optimistic update in both list and open modal
      setTickets(prev => prev.map(t => t._id === id ? { ...t, status: 'solved' } : t));
      setSelectedTicket(prev => prev && prev._id === id ? { ...prev, status: 'solved' } : prev);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteTicket(id);
    setTickets(prev => prev.filter(t => t._id !== id));
    setTotal(prev => prev - 1);
    setSelectedTicket(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Product Feedback</Typography>

      {/* ── Filter Bar ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Organisation</InputLabel>
              <Select value={filterOrgId} label="Organisation" onChange={e => setFilterOrgId(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {orgList.map(org => (
                  <MenuItem key={org._id} value={org._id}>{org.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={filterType} label="Type" onChange={e => setFilterType(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="bug">Bug / Issue</MenuItem>
                <MenuItem value="enhancement">Enhancement</MenuItem>
                <MenuItem value="feature">New Feature</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select value={filterPriority} label="Priority" onChange={e => setFilterPriority(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="solved">Resolved</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small" label="From" type="date"
              value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small" label="To" type="date"
              value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3} md={1}>
            <Button size="small" onClick={handleClearFilters} variant="text" sx={{ whiteSpace: 'nowrap' }}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Table ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : tickets.length === 0 ? (
        <NoDataFound title="No feedback submissions found" subtitle="Try adjusting your filters." />
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F5F7F9' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Ticket ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Raised By</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map(ticket => {
                  const tc = TYPE_COLORS[ticket.type]     || { main: '#1976d2', bg: '#F5F7F9' };
                  const pc = ticket.priority ? PRIORITY_COLORS[ticket.priority] : null;
                  const sc = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;
                  return (
                    <TableRow
                      key={ticket._id}
                      hover
                      onClick={() => setSelectedTicket(ticket)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{ticket.ticketId}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ticket.title}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '8px', height: '22px', borderRadius: '4px', backgroundColor: tc.bg, border: `1px solid ${tc.main}33`, fontSize: '12px', fontWeight: 500, color: tc.main, whiteSpace: 'nowrap' }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: tc.main, flexShrink: 0 }} />
                            {TYPE_LABELS[ticket.type] || ticket.type}
                        </Box>
                    </TableCell>
                      <TableCell>
                        {pc && ticket.priority ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '8px', height: '22px', borderRadius: '4px', backgroundColor: pc.bg, border: `1px solid ${pc.main}33`, fontSize: '12px', fontWeight: 500, color: pc.main, whiteSpace: 'nowrap' }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: pc.main, flexShrink: 0 }} />
                            {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                            </Box>
                        ) : (
                            <Box sx={{ pl: '4px', color: '#aaa', fontSize: '13px' }}>—</Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>{ticket.orgName || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>{ticket.userName || '—'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '8px', height: '22px', borderRadius: '4px', backgroundColor: sc.bg, border: `1px solid ${sc.main}33`, fontSize: '12px', fontWeight: 500, color: sc.main, whiteSpace: 'nowrap' }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: sc.main, flexShrink: 0 }} />
                            {ticket.status === 'solved' ? 'Solved' : 'Open'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {total > ITEMS_PER_PAGE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={Math.ceil(total / ITEMS_PER_PAGE)}
                page={page}
                onChange={handlePageChange}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <TicketDetailModal
        ticket={selectedTicket}
        open={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        isAdmin={true}
        onSolve={handleSolve}
        onDelete={handleDelete}
      />
    </Box>
  );
};

export default ProductFeedbackAdminView;